import {Injectable, inject} from '@angular/core';
import {CalendarOptions, FormatterInput} from '@fullcalendar/core';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import deLocale from '@fullcalendar/core/locales/de';
import enLocale from '@fullcalendar/core/locales/en-gb';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../../environments/environment';
import {BERLIN_TZ} from './event-calendar.constants';

export type CalendarView = 'day' | 'week' | 'month';

@Injectable({providedIn: 'root'})
export class EventCalendarConfigService {
  private translate = inject(TranslateService);

  public buildBaseOptions(locale: 'de' | 'en'): CalendarOptions {
    return {
      height: '100%',
      themeSystem: 'bootstrap5',
      timeZone: BERLIN_TZ,
      plugins: [resourceTimelinePlugin],
      initialView: 'resourceTimelineDay',
      schedulerLicenseKey: environment.fullCalendar.licenseKey,
      firstDay: 1,
      headerToolbar: {left: 'prev,next today title', right: 'day,week,month'},
      editable: false,
      selectable: false,
      locales: [deLocale, enLocale],
      locale,
      lazyFetching: true,
      nowIndicator: true,
      showNonCurrentDates: true,
      slotEventOverlap: true,
      stickyHeaderDates: true,
      expandRows: true,
      resourceAreaWidth: '15%',
      eventMinWidth: 0,
      resourceOrder: 'order,secondaryOrder',
    };
  }

  public viewConfig(view: CalendarView, locale: 'de' | 'en'): Partial<CalendarOptions> {
    const slotLabelFormat = this.slotLabelFormat(view, locale);
    switch (view) {
      case 'day':
        return {
          slotLabelInterval: {hours: 1},
          slotDuration: '00:30:00',
          titleFormat: {year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'long'},
          slotLabelFormat,
        };
      case 'week':
        return {
          slotLabelInterval: {hours: 6},
          slotDuration: '06:00:00',
          titleFormat: {year: 'numeric', month: 'long', day: '2-digit'},
          slotLabelFormat,
        };
      case 'month':
        return {
          slotLabelInterval: {hours: 24},
          slotDuration: '24:00:00',
          titleFormat: {year: 'numeric', month: 'long'},
          slotLabelFormat,
        };
    }
  }

  public buttonLabels(locale: 'de' | 'en'): Record<CalendarView, string> {
    return locale === 'de'
      ? {day: 'Tag', week: 'Woche', month: 'Monat'}
      : {day: 'Day', week: 'Week', month: 'Month'};
  }

  private slotLabelFormat(view: CalendarView, locale: 'de' | 'en'): FormatterInput[] {
    switch (view) {
      case 'week':
        return [
          {weekday: 'long', month: '2-digit', day: '2-digit', omitCommas: true},
          {hour: 'numeric', minute: 'numeric', hour12: locale === 'en'},
        ];
      case 'day':
        return [{hour: 'numeric', minute: 'numeric', hour12: locale === 'en'}];
      case 'month':
        return [
          {week: 'short'},
          {weekday: 'short', day: '2-digit', omitCommas: true},
        ];
    }
  }
}
